import {
  useRef, useState, Dispatch, SetStateAction, MutableRefObject,
} from 'react'

import useSetState from '@hooks/useSetState'

/*
 * Use this hook when you want to keep track of how many
 * modals have been opened and which one is the base (first opened)
 *
 * Wrap the page in the ModalContext.Provider (from @domain-group/suit-theme) to
 * provide context to the SidebarModal
 *
 */

const registerModal = (
  modalKey: string,
  showModal: boolean,
  setOpenModalKeys: Dispatch<SetStateAction<string[]>>,
) => {
  setOpenModalKeys((openModalKeys: string[]) => {
    const updatedOpenKeys = [...openModalKeys]

    // Add the key to the open modals if visible, otherwise remove
    const index = openModalKeys.findIndex((key: string) => key === modalKey)
    if (showModal && index === -1){
      updatedOpenKeys.push(modalKey)
    }

    // Remove the modal key when closed
    if (!showModal && index !== -1){
      updatedOpenKeys.splice(index, 1)
    }

    return updatedOpenKeys
  })
}

const defaultState = {
  modalData: {},
}

const setModalData = (
  modalKey: string,
  data: any,
  stateRef: MutableRefObject<typeof defaultState>,
  setState: (state: Partial<typeof defaultState>) => void,
) => {
  const { modalData } = stateRef.current

  const updatedData = {
    ...modalData,
    [modalKey]: data,
  }

  setState({ modalData: updatedData })
}

const closeAllModals = (openModalKeys: Array<string>, setComponentState?: (state: Record<string, boolean>) => void) => {
  if (typeof setComponentState === 'undefined' || setComponentState === null){
    return
  }

  openModalKeys.forEach((key) => {
    setComponentState({ [`show${key}`]: false })
  })
}

type UseModalsOptions = {
  setState?: (state: Record<string, boolean>) => void,
}

function useModals(options?: UseModalsOptions) {
  const { setState: setComponentState } = options || {}

  // Using useState here so we can always check latest state
  // in the useState callback
  const [openModalKeys, setOpenModalKeys] = useState<string[]>([])
  const stateRef = useRef(defaultState)

  const [state, setState] = useSetState(defaultState)
  stateRef.current = state

  const baseModalKey = openModalKeys[0] || null
  const topModalKey = openModalKeys[openModalKeys.length - 1]

  return {
    baseModalKey,
    callbacks: {
      closeAllModals: () => closeAllModals(openModalKeys, setComponentState),
      registerModal: (modalKey: string, showModal: boolean) => (
        registerModal(modalKey, showModal, setOpenModalKeys)
      ),
      setModalData: (modalKey: string, data: { [key: string]: any }) => (
        setModalData(modalKey, data, stateRef, setState)
      ),
      setState,
    },
    openModalKeys,
    topModalKey,
    ...state,
  }
}

export default useModals
